/**
 * ModernTemplate — 모던 청첩장 레이아웃
 *
 * 풀블리드 히어로 + 카드형 예식 안내 + 캐러셀 갤러리.
 * 같은 빌딩 블록을 다른 layout prop + 다른 순서로 조합.
 */

import { useMemo } from 'react'
import type { TemplateProps } from './types.js'
import { FONT_FAMILY_MAP, TEXT_SCALE_MAP, FADE_SPEED_MAP } from './blocks/formatters.js'
import { useFontLoader } from './useFontLoader.js'
import { SectionWrapper } from './blocks/SectionWrapper.js'
import { HeroBlock } from './blocks/HeroBlock.js'
import { GreetingBlock } from './blocks/GreetingBlock.js'
import { CeremonyBlock } from './blocks/CeremonyBlock.js'
import { MapBlock } from './blocks/MapBlock.js'
import { GalleryBlock } from './blocks/GalleryBlock.js'
import { ContactsBlock } from './blocks/ContactsBlock.js'
import { AccountsBlock } from './blocks/AccountsBlock.js'
import { TimelineBlock } from './blocks/TimelineBlock.js'
import { ScheduleBlock } from './blocks/ScheduleBlock.js'
import { RsvpBlock } from './blocks/RsvpBlock.js'
import { GuestbookBlock } from './blocks/GuestbookBlock.js'
import { NoticesBlock } from './blocks/NoticesBlock.js'
import { FooterBlock } from './blocks/FooterBlock.js'
import { FadeIn } from './blocks/FadeIn.js'
import css from './ModernTemplate.module.css'

function sectionSpacingStyle(
  sectionSpacing: Record<string, string> | undefined,
  sectionId: string,
): React.CSSProperties | undefined {
  const v = sectionSpacing?.[sectionId]
  if (!v || v === 'default') return undefined
  const px = v === 'lg' ? '40px' : v === 'xl' ? '64px' : `${Math.min(Number(v) || 0, 200)}px`
  return { '--section-spacing': px } as React.CSSProperties
}

export function ModernTemplate({ invitation, config, activeSection, onSectionClick, mode }: TemplateProps) {
  const enabledSections = invitation.enabledSections
  const blockProps = { invitation, activeSection, onSectionClick, mode, css } as const

  const settings = invitation.settings ?? {}
  const fontFamily = settings.fontFamily ? FONT_FAMILY_MAP[settings.fontFamily] : undefined
  const textScale = settings.textScale ? TEXT_SCALE_MAP[settings.textScale] ?? 1 : 1
  const backgroundColor = config.style.backgroundColor ?? settings.backgroundColor
  const textColor = settings.textColor
  const ss = settings.sectionSpacing
  const fadeDuration = settings.fadeSpeed ? FADE_SPEED_MAP[settings.fadeSpeed] : undefined

  useFontLoader(settings.fontFamily)

  const rootStyle = useMemo(() => {
    const s: React.CSSProperties = {}
    if (fontFamily) s.fontFamily = fontFamily
    if (textScale !== 1) s.fontSize = `${textScale}em`
    if (backgroundColor) s.backgroundColor = backgroundColor
    if (textColor) s.color = textColor
    return s
  }, [fontFamily, textScale, backgroundColor, textColor])

  return (
    <div className={css.invitation} style={rootStyle}>
      <HeroBlock {...blockProps} layout="fullbleed" />

      {enabledSections?.greeting !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'greeting')}>
          <GreetingBlock {...blockProps} layout="framed" />
        </FadeIn>
      )}

      <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'ceremony')}>
        <SectionWrapper sectionId="ceremony" activeSection={activeSection} onSectionClick={onSectionClick} mode={mode}>
          <CeremonyBlock invitation={invitation} layout="card-pair" css={css} />
          <MapBlock invitation={invitation} mode={mode} css={css} />
        </SectionWrapper>
      </FadeIn>

      {enabledSections?.gallery !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'gallery')}>
          <GalleryBlock {...blockProps} layout="carousel" />
        </FadeIn>
      )}

      {enabledSections?.timeline !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'timeline')}>
          <TimelineBlock {...blockProps} />
        </FadeIn>
      )}

      {enabledSections?.schedule !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'schedule')}>
          <ScheduleBlock {...blockProps} />
        </FadeIn>
      )}

      {enabledSections?.contacts !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'contacts')}>
          <ContactsBlock {...blockProps} />
        </FadeIn>
      )}

      {enabledSections?.account !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'accounts')}>
          <AccountsBlock {...blockProps} layout="list" />
        </FadeIn>
      )}

      {enabledSections?.rsvp !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'rsvp')}>
          <RsvpBlock {...blockProps} />
        </FadeIn>
      )}

      {enabledSections?.guestbook !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'guestbook')}>
          <GuestbookBlock {...blockProps} />
        </FadeIn>
      )}

      {enabledSections?.notices !== false && (
        <FadeIn duration={fadeDuration} style={sectionSpacingStyle(ss, 'notices')}>
          <NoticesBlock {...blockProps} layout="list" />
        </FadeIn>
      )}

      <FooterBlock invitation={invitation} />
    </div>
  )
}
